import { useCallback, useEffect, useMemo } from "react";

import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import browser from "web/script/utils/browser";
import { sendPerformanceMetrics } from "web/script/utils/performance-metrics";
import url from "web/script/utils/url";
import { TrackLeadPage } from "web/types/serializers";

export function useTrackLead({
    is_returning: isReturning,
    is_sold_out: soldOutLead,
    via_interstitial: viaInterstitial,
    origin_url: originUrl,
    redirect_url: redirectUrl,
    return_url: returnUrl,
    product_id: productId,
    sale_price: salePrice,
    is_css_lead: isCssLead,
    retailer_slug: retailerSlug,
    designer_slug: designerSlug,
    product_category: productCategory,
    product_gender: productGender,
    query_params,
}: TrackLeadPage): void {
    const queryParams = query_params as unknown as Record<string, string>; // Django ts serializer quirk where DictField is inferred as List
    const atcGrouping = queryParams?.atc_grouping;

    const category = isReturning ? "NAVIGATION" : "AFFILIATE";
    const action = useMemo(() => {
        if (isReturning) {
            return "RETAILER_BOUNCE";
        } else if (soldOutLead) {
            return "LEAD_SOLD_OUT";
        } else {
            return "LEAD";
        }
    }, [isReturning, soldOutLead]);

    const handleReturn = useCallback(() => {
        if (globals.window.opener) {
            // if going back to a feed instead of a PDP then add productId to query string and replace history
            globals.window.close();
        } else if (viaInterstitial) {
            globals.window.close();
        } else {
            globals.window.location = originUrl as string & Location;
        }
    }, [viaInterstitial, originUrl]);

    const redirect = useCallback(
        (clientId: string) => {
            // For Google CSS shopping we do not want the user to return back on lyst,
            // so this ensures it is not added to the browser history
            const parsed = url.parse(redirectUrl);
            parsed.searchParams.set("client_id", clientId);
            const clientIdUrl = url.unparse(parsed);

            const webEnablePerformanceMetricsTracking = environment.getFeature(
                "web_enable_performance_metrics_tracking"
            );

            if (webEnablePerformanceMetricsTracking) {
                sendPerformanceMetrics("interstitial");
            }

            if (atcGrouping && atcGrouping === "Google-PLA-CSS") {
                globals.window.location.replace(clientIdUrl);
            } else {
                globals.window.location.assign(clientIdUrl);
            }
        },
        [redirectUrl, atcGrouping]
    );

    const onTrackingFinish = useCallback(
        (clientId: string | undefined) => {
            if (!redirectUrl) {
                return;
            }

            if (isReturning) {
                handleReturn();
            } else {
                redirect(clientId || "null");
            }
        },
        [handleReturn, redirect, isReturning, redirectUrl]
    );

    const updateHistoryState = useCallback(() => {
        if (["Google-PLA-CSS", "Bing-PLA", "css"].indexOf(atcGrouping) > -1) {
            // PAID-1170
            // Instead of pushing the return redirect url to the history stack, update the
            // most recent entry with the origin url.
            // This should remedy the confusing back button behaviour that has caused users
            // to get stuck on this page when hitting the back button.
            //
            // Also persist existing query param values to ensure that it is tracked
            // as CSS activity in analytics.
            const replaceUrl = originUrl + globals.window.location.search;
            window.history.replaceState(null, document.title, replaceUrl);
        } else {
            // 1. Do it twice, because... chrome bug (when going back it seems to
            //    skip over the first page)
            // 2. Need to use the actual history API here and not our internal wrapper
            //    module as that actually prevents the creation of duplicate history entries.
            window.history.pushState(null, document.title, returnUrl);
            window.history.pushState(null, document.title, returnUrl);
        }
    }, [originUrl, returnUrl, atcGrouping]);

    useEffect(() => {
        if (!isReturning && browser.supportsHistory) {
            updateHistoryState();
        }

        // Set a timeout to send analytics event
        // if there is no redirect happening
        const timeoutId = setTimeout(() => {
            analytics.event(action, "timeout", productId ? String(productId) : undefined, true);
            onTrackingFinish("null");
        }, 10000);

        const runSequentially = async (): Promise<string | undefined> => {
            const [clientId] = await Promise.all([
                analytics.getGAClientID(),
                analytics.event(
                    category,
                    action,
                    productId ? String(productId) : undefined,
                    false,
                    {
                        sale_price: salePrice,
                        isCssLead: isCssLead,
                        retailer_slug: retailerSlug,
                        designer_slug: designerSlug,
                        category: productCategory,
                        gender: productGender,
                        product_id: productId,
                        atc_grouping: atcGrouping,
                    }
                ),
            ]);

            await analytics.whenQueueIsEmpty();

            return clientId;
        };

        runSequentially()
            .then((clientId) => {
                clearTimeout(timeoutId);
                onTrackingFinish(clientId);
            })
            .catch(() => {
                clearTimeout(timeoutId);
                // explicitly pass null here as we won't want whatever error
                // gets passed to this handler to be confused with the client id
                onTrackingFinish("null");
            });

        return () => {
            clearTimeout(timeoutId);
        };
    }, [
        isReturning,
        updateHistoryState,
        action,
        category,
        productId,
        salePrice,
        isCssLead,
        retailerSlug,
        designerSlug,
        productCategory,
        productGender,
        onTrackingFinish,
        atcGrouping,
    ]);
}
